import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/context/LanguageContext";

// Pages
import HomePage from "@/pages/HomePage";
import ServicePage from "@/pages/ServicePage";
import CityServicePage from "@/pages/CityServicePage";
import BlogPage from "@/pages/BlogPage";
import BlogArticlePage from "@/pages/BlogArticlePage";
import SEOPage from "@/pages/SEOPage";
import NewSEOPage from "@/pages/NewSEOPage";
import FrSEOPage from "@/pages/FrSEOPage";
import ServiceSEOPage from "@/pages/ServiceSEOPage";
import { SEO_PAGES } from "@/data/seoPages";
import { NEW_SEO_PAGE_SLUGS } from "@/data/newSeoPages";
import { FR_SEO_PAGE_SLUGS } from "@/data/frSeoPages";
import { SERVICES_SEO_PAGE_SLUGS } from "@/data/servicesSeoPages";

// Service prefixes for route matching
const SERVICE_PREFIXES_FR = [
  "nettoyage-appartement-",
  "nettoyage-villa-",
  "menage-airbnb-",
  "nettoyage-apres-chantier-",
  "nettoyage-fin-location-",
  "conciergerie-airbnb-"
];

const SERVICE_PREFIXES_EN = [
  "apartment-cleaning-",
  "villa-cleaning-",
  "airbnb-cleaning-",
  "post-construction-cleaning-",
  "end-of-rental-cleaning-",
  "airbnb-concierge-"
];

// SEO page slugs
const SEO_PAGE_SLUGS = SEO_PAGES.map(p => p.slug);

// Route matcher component
const RouteResolver = () => {
  const location = useLocation();
  const path = location.pathname;
  const cleanPath = path.replace(/^\//, '');
  
  // Check if it's a French SEO page (under /fr/ path)
  if (cleanPath.startsWith('fr/') && FR_SEO_PAGE_SLUGS.includes(cleanPath)) {
    return <FrSEOPage />;
  }
  
  // Check if it's a new SEO page (under /seo/ path)
  if (cleanPath.startsWith('seo/') && NEW_SEO_PAGE_SLUGS.includes(cleanPath)) {
    return <NewSEOPage />;
  }
  
  // Check if it's a services SEO page (19 new pages at root level)
  if (SERVICES_SEO_PAGE_SLUGS.includes(cleanPath)) {
    return <ServiceSEOPage />;
  }
  
  // Check if it's an existing SEO page
  if (SEO_PAGE_SLUGS.includes(cleanPath)) {
    return <SEOPage />;
  }
  
  // Check French city-service routes
  for (const prefix of SERVICE_PREFIXES_FR) {
    if (path.startsWith('/' + prefix)) {
      return <CityServicePage />;
    }
  }
  
  // Check English city-service routes
  for (const prefix of SERVICE_PREFIXES_EN) {
    if (path.startsWith('/en/' + prefix)) {
      return <CityServicePage />;
    }
  }
  
  // Default to homepage
  return <HomePage />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* French Routes */}
      <Route path="/" element={<HomePage />} />
      
      {/* French Service Pages */}
      <Route path="/services/:slug" element={<ServicePage />} />
      
      {/* French Blog */}
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogArticlePage />} />

      {/* English Routes */}
      <Route path="/en" element={<HomePage />} />
      <Route path="/en/" element={<HomePage />} />
      
      {/* English Service Pages */}
      <Route path="/en/services/:slug" element={<ServicePage />} />
      
      {/* English Blog */}
      <Route path="/en/blog" element={<BlogPage />} />
      <Route path="/en/blog/:slug" element={<BlogArticlePage />} />

      {/* Catch-all for city-service pages and other routes */}
      <Route path="*" element={<RouteResolver />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <LanguageProvider>
            <AppRoutes />
          </LanguageProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
